import { useState, FormEvent } from 'react';
import { CreditCard, ShieldAlert, Loader2, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePaymentMethodsQuery } from '@/client/api/payment/payment.query';
import {
  useDeletePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
} from '@/client/api/payment/payment.mutation';
import { PaymentMethod } from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  OrgRelationshipLink,
  RelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing payment methods requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const getErrorCode = (error: unknown): string | undefined => {
  const data = (error as { data?: { error?: string }; status?: number })?.data;
  return data?.error;
};

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const PaymentMethodsPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Saved cards' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const managePerm = useHasPermission('payment.manage');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { state, patch } = usePaymentListUrlState();
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const uid = state.uid ? Number(state.uid) : undefined;
  const oid = state.oid ? Number(state.oid) : undefined;
  const bpmidFromUrl = state.bpmid;
  const hasTarget = !!(uid || oid);

  const { data: cards = [], isLoading, isFetching } = usePaymentMethodsQuery(
    { uid, oid, include_deleted: includeDeleted },
    hasTarget && canView,
  );

  const setDefault = useSetDefaultPaymentMethodMutation();
  const deleteCard = useDeletePaymentMethodMutation();

  const handleLookup = (e: FormEvent) => {
    e.preventDefault();
    if (!state.uid.trim() && !state.oid.trim()) {
      toast.error('Enter a UID or OID', { richColors: true });
      return;
    }
    patch({ uid: state.uid.trim() || undefined, oid: state.oid.trim() || undefined }, false);
  };

  const ownerSummary = cards.find((c) => c.user_summary || c.org_summary);
  const ownerLabel = (
    <div className="flex flex-wrap items-center gap-3">
      {uid != null && <UserRelationshipLink uid={uid} summary={ownerSummary?.user_summary} />}
      {oid != null && <OrgRelationshipLink oid={oid} summary={ownerSummary?.org_summary} />}
      <RelationshipLink
        kind="wallet"
        id={uid ?? oid ?? ''}
        label="View wallets"
        extra={{ uid, oid }}
        className="text-blue-700"
      />
    </div>
  );

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <CreditCard className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Saved cards</h1>
          </div>
          <p className="text-sm text-gray-500">
            Look up a user's or organization's saved payment methods — set default or remove.
          </p>
        </div>

        <form
          onSubmit={handleLookup}
          className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>User ID</Label>
              <Input
                value={state.uid}
                onChange={(e) => patch({ uid: e.target.value }, false)}
                placeholder="User ID"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Organization ID</Label>
              <Input
                value={state.oid}
                onChange={(e) => patch({ oid: e.target.value }, false)}
                placeholder="Organization ID"
                inputMode="numeric"
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 pb-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                Include deleted
              </label>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" disabled={isFetching}>
                {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Look up
              </Button>
            </div>
          </div>
        </form>

        {hasTarget && (
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">{ownerLabel}</div>
        )}

        {hasTarget && isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {hasTarget && !isLoading && cards.length === 0 && (
          <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No payment methods found.
          </div>
        )}

        {hasTarget && !isLoading && cards.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50">
                    {['Card', 'Label', 'Channel', 'Expiry', 'Default', 'Added', 'BPMID', 'Actions'].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {cards.map((card: PaymentMethod) => {
                    const isDefault = card.is_default === 1 || card.is_default === true;
                    const deleted = card.deleted === 1 || card.deleted === true;
                    const isHighlighted = bpmidFromUrl != null && card.bpmid === bpmidFromUrl;
                    return (
                      <tr
                        key={card.bpmid}
                        className={`${deleted ? 'opacity-50' : ''} ${
                          isHighlighted ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-blue-500" />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {card.brand || 'Card'}
                              </p>
                              <p className="font-mono text-xs text-gray-400">
                                •••• {card.last4 || '—'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="max-w-[160px] truncate px-6 py-4 text-sm text-gray-700">
                          {card.label || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {card.channel || card.provider || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {card.exp_month && card.exp_year
                            ? `${String(card.exp_month).padStart(2, '0')}/${card.exp_year}`
                            : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {isDefault ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                              <Star className="h-3 w-3" />
                              Default
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(card.date_added)}
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-gray-400">{card.bpmid}</td>
                        <td className="px-6 py-4">
                          {canManage && !deleted && (
                            <div className="flex flex-wrap gap-1.5">
                              {!isDefault && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={setDefault.isPending}
                                  onClick={async () => {
                                    try {
                                      await setDefault.mutateAsync(card.bpmid);
                                      toast.success('Default card updated', { richColors: true });
                                    } catch (error: any) {
                                      toast.error(error?.message || 'Failed to set default', {
                                        richColors: true,
                                      });
                                    }
                                  }}
                                >
                                  Set default
                                </Button>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={deleteCard.isPending}
                                onClick={async () => {
                                  try {
                                    await deleteCard.mutateAsync(card.bpmid);
                                    toast.success('Card removed', { richColors: true });
                                  } catch (error: any) {
                                    const code = getErrorCode(error);
                                    if (
                                      code === 'payment_method_in_use' ||
                                      (error as { status?: number })?.status === 409
                                    ) {
                                      toast.error(
                                        'This card is in use (active subscription or pending payment). Unlink it first.',
                                        { richColors: true, duration: 8000 },
                                      );
                                    } else {
                                      toast.error(error?.message || 'Failed to delete card', {
                                        richColors: true,
                                      });
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="mr-1 h-3.5 w-3.5" />
                                Delete
                              </Button>
                            </div>
                          )}
                          {deleted && <span className="text-xs text-gray-400">Deleted</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsPage;
